import { UserRole } from "@features/settings/types/settings.types";
import { useProfile } from "@shared/hooks/useProfile";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { db } from "../../../config/firebase";
import {
  SUPPORT_MESSAGES_COLLECTION,
  SUPPORT_TICKETS_COLLECTION,
} from "../constants/collections";

interface SupportUnreadContextType {
  unreadCount: number;
}

const SupportUnreadContext = createContext<SupportUnreadContextType>({
  unreadCount: 0,
});

export const useSupportUnreadContext = () => useContext(SupportUnreadContext);

export const SupportUnreadProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useProfile();
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const prevCountRef = useRef<number>(0);
  const initializedRef = useRef<boolean>(false);
  const soundTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    const unreadCountsMap: Record<string, number> = {};
    const messageUnsubscribes: (() => void)[] = [];
    let ticketUnsubscribe: (() => void) | null = null;

    if (user?.role === UserRole.ADMIN) {
      const supportTicketsCollection = collection(
        db,
        SUPPORT_TICKETS_COLLECTION
      );

      ticketUnsubscribe = onSnapshot(supportTicketsCollection, (snapshot) => {
        messageUnsubscribes.forEach((unsub) => unsub());
        messageUnsubscribes.length = 0;

        snapshot.forEach((ticketDoc) => {
          const ticketId = ticketDoc.id;
          const messagesCollection = collection(
            db,
            SUPPORT_TICKETS_COLLECTION,
            ticketId,
            SUPPORT_MESSAGES_COLLECTION
          );

          const messagesQuery = query(
            messagesCollection,
            where("read", "==", false),
            where("senderId", "!=", user.id)
          );

          const messageUnsubscribe = onSnapshot(
            messagesQuery,
            (messagesSnapshot) => {
              unreadCountsMap[ticketId] = messagesSnapshot.size;

              const totalCount = Object.values(unreadCountsMap).reduce(
                (sum: number, count: number) => sum + count,
                0
              );

              if (initializedRef.current && totalCount > prevCountRef.current) {
                if (soundTimeoutRef.current) {
                  clearTimeout(soundTimeoutRef.current);
                }

                soundTimeoutRef.current = setTimeout(() => {
                  const audio = new Audio(
                    "/assets/audio/notification-support.mp3"
                  );
                  audio
                    .play()
                    .catch((error) =>
                      console.error("Failed to play notification sound:", error)
                    );

                  soundTimeoutRef.current = null;
                }, 300);
              }

              prevCountRef.current = totalCount;
              if (!initializedRef.current) {
                initializedRef.current = true;
              }

              setUnreadCount(totalCount);
            }
          );

          messageUnsubscribes.push(messageUnsubscribe);
        });
      });
    } else if (user?.role === UserRole.STUDENT) {
      const studentTicketsQuery = query(
        collection(db, SUPPORT_TICKETS_COLLECTION),
        where("userId", "==", user.id)
      );

      ticketUnsubscribe = onSnapshot(studentTicketsQuery, (snapshot) => {
        messageUnsubscribes.forEach((unsub) => unsub());
        messageUnsubscribes.length = 0;

        snapshot.forEach((ticketDoc) => {
          const ticketId = ticketDoc.id;
          const messagesCollection = collection(
            db,
            SUPPORT_TICKETS_COLLECTION,
            ticketId,
            SUPPORT_MESSAGES_COLLECTION
          );

          const messagesQuery = query(
            messagesCollection,
            where("read", "==", false),
            where("senderId", "!=", user.id)
          );

          const messageUnsubscribe = onSnapshot(
            messagesQuery,
            (messagesSnapshot) => {
              unreadCountsMap[ticketId] = messagesSnapshot.size;

              const totalCount = Object.values(unreadCountsMap).reduce(
                (sum: number, count: number) => sum + count,
                0
              );

              if (initializedRef.current && totalCount > prevCountRef.current) {
                if (soundTimeoutRef.current) {
                  clearTimeout(soundTimeoutRef.current);
                }

                soundTimeoutRef.current = setTimeout(() => {
                  const audio = new Audio(
                    "/assets/audio/notification-support.mp3"
                  );
                  audio
                    .play()
                    .catch((error) =>
                      console.error("Failed to play notification sound:", error)
                    );

                  soundTimeoutRef.current = null;
                }, 300);
              }

              prevCountRef.current = totalCount;
              if (!initializedRef.current) {
                initializedRef.current = true;
              }

              setUnreadCount(totalCount);
            }
          );

          messageUnsubscribes.push(messageUnsubscribe);
        });
      });
    }

    return () => {
      if (ticketUnsubscribe) {
        ticketUnsubscribe();
      }
      messageUnsubscribes.forEach((unsub) => unsub());
    };
  }, [user?.id, user?.role]);

  return (
    <SupportUnreadContext.Provider value={{ unreadCount }}>
      {children}
    </SupportUnreadContext.Provider>
  );
};

export const useSupportUnreadCount = () => {
  const { unreadCount } = useSupportUnreadContext();
  return unreadCount;
};
